import {
  Button,
  Checkbox,
  Input,
  message,
  Select,
  Switch,
  Transfer,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FieldRegister from 'views/common/FieldRegister';
import TextField from 'views/common/TextField';
import Loading from 'views/common/Loading';
import { useValidate, ValidateContext } from 'views/context/ValidateContext';
import API from 'api';
import {
  validateEmail,
  validatePassword,
  validatePermissionAndRoleEmpty,
  validateRequired,
} from 'utils/validate';
import { CheckOutlined } from '@ant-design/icons';
import StatusHeader from 'views/common/StatusHeader';
import { useSelector } from 'react-redux';
import { checkHasAnyPermission, PERMISSION } from 'constants/permission';
import AccessControl from 'views/common/AccessControl';
import useEventCallback from 'hooks/useEventCallback';

const transformBody = (state = {}) => {
  const { username, fullName, email, password, roles, permissions, userId } =
    state;
  return {
    username,
    fullName,
    email,
    password,
    roles,
    permissions,
    userId,
  };
};

const transformDetail = (data = {}) => {
  return { ...data };
};

const CreateUsers = () => {
  const validate = useValidate('create-user');
  const [state, setState] = useState({});
  const { pathname } = useLocation();
  const isCreatePage = pathname.includes('create');
  const navigator = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [preload, setPreload] = useState({});
  const { userId } = useParams();
  const { permissions } = useSelector((state) => state.auth);

  const onChangeField = useEventCallback(
    (e, field) => {
      setState((s) => ({ ...s, [field]: e.target.value }));
    },
    [state]
  );

  const onChangePermissions = useEventCallback(
    (options) => {
      setState((s) => ({ ...s, permissions: options }));
    },
    [state]
  );

  const onChangeRoles = useEventCallback(
    (options) => {
      setState((s) => ({ ...s, roles: options }));
    },
    [state]
  );

  const onClickSave = async () => {
    const error = validate.validateFields();
    if (Object.values(error || {})?.length > 0) {
      return message.error('Authorization Invalid.');
    }
    setLoading(true);
    let res;
    if (isCreatePage) {
      res = await API.adminCreateUserInternal(transformBody(state));
    } else {
      res = await API.adminUpdateUser(transformBody(state));
    }

    setLoading(false);

    if (res.status === 200) {
      message.success(
        isCreatePage ? 'Create user success' : 'Update user success'
      );
      return navigator('/user');
    }
    message.error(res.message);
  };

  const onCancel = () => {
    navigator('/user');
  };

  const preloadPermissions = async () => {
    const res = await API.getAllAuthorization({
      limit: 1000,
    });

    if (res.status === 200) {
      const permissions = res.data.data.filter((item) => item.type !== 'ROLE');
      const roles = res.data.data.filter((item) => item.type === 'ROLE');
      setPreload({ permissions, roles });
    }
  };

  const getDetail = async () => {
    const res = await API.adminGetDetailUser(userId);
    if (res.status === 200) {
      setState(transformDetail(res.data));
    }
  };

  const loadData = async () => {
    setLoading(true);
    if (!preload.permissions) await preloadPermissions();
    if (!isCreatePage) await getDetail();
    setLoading(false);
  };

  const filterTransfer = (input, option) => {
    const v = input.toLowerCase();
    return (
      option?.code?.toLowerCase()?.includes(v) ||
      option?.description?.toLowerCase()?.includes(v)
    );
  };

  const onDisabledUser = async () => {
    setLoading(true);
    const res = await API.adminActiveDeactiveUser(userId, !state.disabled);

    setLoading(false);
    if (res.status === 200) {
      return setState((state) => ({ ...state, disabled: !state.disabled }));
    }
    message.error(res.message);
  };

  const getStatus = () => {
    if (isCreatePage) return 'Creating';
    if (state.disabled) return 'Inactive';
    return 'Active';
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [userId]);

  const permissionDisabled = !checkHasAnyPermission(permissions, [
    isCreatePage ? PERMISSION.CREATE_INTERNAL_USERS : PERMISSION.UPDATE_USERS,
  ]);

  return (
    <ValidateContext.Provider value={validate}>
      <div className="create-users" spellCheck={false}>
        {isLoading && <Loading />}
        <div className="create-users__title">
          {isCreatePage ? 'Create New User' : 'Edit User'}
        </div>
        <StatusHeader
          options={['Creating', 'Active', 'Inactive']}
          status={getStatus()}
        />
        {!isCreatePage && (
          <div className="create-users__block">
            <AccessControl permissions={[PERMISSION.ACTIVE_INACTIVE_USERS]}>
              <Switch checked={!state.disabled} onChange={onDisabledUser} />
            </AccessControl>
          </div>
        )}
        <div className="create-users__block">
          <FieldRegister
            name="email"
            validate={[validateRequired('Email'), validateEmail]}
            value={state.email}
            disabled={!isCreatePage || state.disabled}
            onChange={(e) => onChangeField(e, 'email')}
          >
            {({ value, onChange, disabled, onBlur, error }) => (
              <TextField
                value={value}
                label="Email"
                placeholder="Enter Email"
                required
                disabled={disabled}
                helperText={error}
                onChange={onChange}
                autoComplete="email"
                name="email"
                onBlur={onBlur}
              />
            )}
          </FieldRegister>
          {isCreatePage && (
            <FieldRegister
              name="password"
              validate={[validateRequired('Password'), validatePassword]}
              value={state.password}
              onChange={(e) => onChangeField(e, 'password')}
            >
              {({ value, onChange, onBlur, error }) => (
                <TextField
                  label="Password"
                  placeholder="Enter Password"
                  required
                  helperText={error}
                >
                  <Input.Password
                    value={value}
                    onChange={onChange}
                    autoComplete="section-create password"
                    name="create-pass"
                    onBlur={onBlur}
                  />
                </TextField>
              )}
            </FieldRegister>
          )}

          <FieldRegister
            name="Fullname"
            validate={[validateRequired('Fullname')]}
            value={state.fullName}
            disabled={state.disabled || permissionDisabled}
            onChange={(e) => onChangeField(e, 'fullName')}
          >
            {({ value, onChange, disabled, onBlur, error }) => (
              <TextField
                value={value}
                label="Fullname"
                placeholder="Enter Fullname"
                required
                disabled={disabled}
                helperText={error}
                onChange={onChange}
                autoComplete="name"
                name="fullName"
                onBlur={onBlur}
              />
            )}
          </FieldRegister>

          {!isCreatePage && (
            <>
              <TextField
                value={state.company}
                label="Company"
                placeholder="Enter company"
                disabled
              />
              <TextField label="Instant Messenger">
                <div className="d-flex intant-messenger">
                  <Select
                    className="flex-1 intant-messenger__select"
                    value={state.instantMessengerType}
                    disabled
                  >
                    <Select.Option value="SKYPE">Skype</Select.Option>
                    <Select.Option value="GOOGLE_TALK">
                      Google Talk
                    </Select.Option>
                    <Select.Option value="JABBER">Jabber</Select.Option>
                    <Select.Option value="AIM">AIM</Select.Option>
                  </Select>
                  <div className="flex-1 intant-messenger__text">
                    <TextField
                      value={state.instantMessengerUsername}
                      disabled
                    />
                  </div>
                </div>
              </TextField>
            </>
          )}

          {!isCreatePage && (
            <>
              <TextField
                label="Account Type"
                value={
                  state.accountType === 'INTERNAL' ? 'Internal' : 'External'
                }
                disabled
              />
              <TextField label="Use Two-Factor Authentication">
                <Checkbox disabled checked={state.use2Fa} />
              </TextField>
            </>
          )}

          <FieldRegister
            name="roles"
            value={state.roles}
            depend={state.permissions}
            validate={validatePermissionAndRoleEmpty}
            disabled={state.disabled || permissionDisabled}
            dataSource={preload.roles}
            onChange={onChangeRoles}
            filterOption={filterTransfer}
          >
            {({
              value,
              onChange,
              disabled,
              onBlur,
              error,
              dataSource,
              filterTransfer,
            }) => (
              <TextField label="Roles" helperText={error}>
                <Transfer
                  className="transfer-component"
                  dataSource={dataSource}
                  showSearch
                  titles={['Avaiable Roles', 'Assigned Roles']}
                  targetKeys={value}
                  onChange={onChange}
                  render={(item) => `${item.code} (${item.description})`}
                  rowKey={(item) => item.code}
                  oneWay
                  filterOption={filterTransfer}
                  disabled={disabled}
                  onBlur={onBlur}
                />
              </TextField>
            )}
          </FieldRegister>

          <FieldRegister
            name="permissions"
            value={state.permissions}
            depend={state.roles}
            validate={validatePermissionAndRoleEmpty}
            disabled={state.disabled || permissionDisabled}
            dataSource={preload.permissions}
            onChange={onChangePermissions}
            filterOption={filterTransfer}
          >
            {({
              value,
              onChange,
              disabled,
              onBlur,
              error,
              dataSource,
              filterTransfer,
            }) => (
              <TextField label="Permissions" helperText={error}>
                <Transfer
                  className="transfer-component"
                  dataSource={dataSource}
                  showSearch
                  titles={['Avaiable Permissions', 'Assigned Permissions']}
                  targetKeys={value}
                  onChange={onChange}
                  render={(item) => `${item.code} (${item.description})`}
                  rowKey={(item) => item.code}
                  oneWay
                  filterOption={filterTransfer}
                  disabled={disabled}
                  onBlur={onBlur}
                />
              </TextField>
            )}
          </FieldRegister>
        </div>
        <div className="create-users__footer">
          <Button onClick={onCancel}>Cancel</Button>
          {!permissionDisabled && (
            <Button
              disabled={state.disabled}
              icon={<CheckOutlined />}
              type="primary"
              onClick={onClickSave}
            >
              Save
            </Button>
          )}
        </div>
      </div>
    </ValidateContext.Provider>
  );
};

export default CreateUsers;
